// src/config/gameProfiles/GameProfile.ts
// -------------------------------------------------------
// The shared contract every game type implements.
// Adding a new game type = one file that implements this interface.
// -------------------------------------------------------

/** Phase names are plain strings — each profile defines its own. */
export type Phase = string;

// ─── Board types ────────────────────────────────────────

export interface BoardBlock {
  type: string;            // e.g. 'hero' | 'assumption' | 'clue' | 'callout' | ...
  content: Record<string, unknown>;  // block-type-specific payload
}

export interface BoardPayload {
  layout: string;          // template name for TemplateSelector / DigiBoardPanel
  blocks: BoardBlock[];    // ordered list of content blocks
  phase: Phase;            // which phase produced this board
}

// ─── Events & State ─────────────────────────────────────

/**
 * A GameEvent describes something that happened in the conversation.
 * The frontend creates these; the profile's `transition()` consumes them.
 */
export interface GameEvent {
  type: string;
  // Typical types per game:
  //   KCV:  'user_response' | 'correct' | 'incorrect' | 'next_confirmed' | 'teach_requested'
  //   DT:   'user_response' | 'ask_question' | 'action_chosen' | 'consequence' | 'success' | 'partial' | 'advance'
  payload?: Record<string, unknown>;
}

/**
 * Accumulated game state maintained by ChatLearningSpace.
 * Profiles read this to decide transitions and build prompts.
 */
export interface GameState {
  phase: Phase;
  turnInPhase: number;     // how many exchanges within the current phase
  totalTurns: number;      // total exchanges since game start
  history: GameEvent[];    // last N events (capped to keep memory small)

  /**
   * Profile-specific accumulator.
   * DT uses this for the assumptions/clues list.
   * KCV uses it for question count, score, etc.
   */
  custom: Record<string, unknown>;
}

// ─── The Profile interface ──────────────────────────────

export interface GameProfile {
  /** Unique id, e.g. 'kcv' | 'design-thinking' | 'lesson' */
  id: string;

  /** Human-readable name shown in UI / logs */
  displayName: string;

  // ── Phase machine ──────────────────────────────────

  /** All phases this game type uses, in canonical order. */
  phases: readonly Phase[];

  /** The phase the game starts in. */
  initialPhase: Phase;

  /**
   * Given current state + an event, return the next phase
   * or `null` to stay in the current phase.
   */
  transition: (state: GameState, event: GameEvent) => Phase | null;

  // ── Board ──────────────────────────────────────────

  /**
   * How the board gets its content:
   *  - 'avatar-driven'  : board payload comes inside the avatar's GPT response (Pipeline B)
   *  - 'inferred'       : board is fetched separately via digiboard.php (Pipeline A)
   */
  boardSource: 'avatar-driven' | 'inferred';

  /** Maps each phase to a layout name (used by TemplateSelector / DigiBoardPanel). */
  layoutByPhase: Record<string, string>;

  /** Block types this profile uses (for documentation / validation). */
  blockTypes: readonly string[];

  // ── Prompt injection ───────────────────────────────

  /**
   * Returns the "flow control" paragraph appended to the system prompt.
   * Replaces the hardcoded `buildControlPrompt()` in ChatLearningSpace.
   */
  buildControlPrompt: (state: GameState, userText: string) => string;

  /**
   * Returns the JSON schema instruction telling the LLM what board payload to produce.
   * Only relevant when boardSource === 'avatar-driven'.
   * For 'inferred' profiles this returns ''.
   */
  buildBoardInstruction: (state: GameState) => string;

  // ── Ready gate ─────────────────────────────────────

  /** If true, the game starts with an intro phase waiting for user readiness. */
  readyGateEnabled: boolean;

  /** Tokens the user can say to indicate they're ready. */
  readyAcceptTokens: readonly string[];

  /** The welcome/intro message shown before the game starts. */
  readyPrompt: string;

  // ── Detection ──────────────────────────────────────

  /**
   * Keywords in the system prompt that trigger auto-detection of this profile.
   * Checked in order; first matching profile wins.
   */
  detectKeywords: readonly string[];

  // ── Gatekeeper (answer-leak prevention) ────────────

  /**
   * Phases where answer content must not appear on the board.
   * The board gatekeeper strips answer-ish fields in these phases.
   */
  noAnswerPhases: readonly Phase[];

  /** Block types allowed on the board during no-answer phases. */
  allowedBlockTypesInNoAnswerPhases: readonly string[];

  /** If true, force meta.mode = 'question' in no-answer phases. */
  forceQuestionMeta: boolean;

  // ── Event extraction ───────────────────────────────

  /**
   * Given the user's text and the current state, produce a GameEvent.
   * This replaces the scattered `isNextConfirm()` / `teachTriggers` logic.
   */
  classifyUserInput: (userText: string, state: GameState) => GameEvent;

  /**
   * Given the assistant's response (and optional script/board),
   * produce a GameEvent describing what happened.
   * Used for phase transitions driven by the LLM's output.
   */
  classifyAssistantResponse: (
    reply: string,
    script: Record<string, unknown> | null,
    state: GameState
  ) => GameEvent;
}

// ─── Helper: create initial GameState ────────────────────

export function createInitialGameState(profile: GameProfile): GameState {
  return {
    phase: profile.initialPhase,
    turnInPhase: 0,
    totalTurns: 0,
    history: [],
    custom: {},
  };
}

// ─── Helper: advance state ──────────────────────────────

export function advanceGameState(
  profile: GameProfile,
  state: GameState,
  event: GameEvent
): GameState {
  const nextPhase = profile.transition(state, event);
  const phaseChanged = nextPhase !== null && nextPhase !== state.phase;

  const nextHistory = [...state.history, event].slice(-20); // keep last 20

  return {
    phase: phaseChanged ? nextPhase! : state.phase,
    turnInPhase: phaseChanged ? 0 : state.turnInPhase + 1,
    totalTurns: state.totalTurns + 1,
    history: nextHistory,
    custom: state.custom, // profiles mutate this via classifyAssistantResponse if needed
  };
}
